import { Fragment, useState } from 'react'

import { EditButton, IllustratedRow, InfoRow, SectionCard } from '@/components/Chat/ConfirmInfoCard'
import { IllustrationName } from '@/components/icons/Illustration'
import { COVERAGE_ICONS } from '@/components/quotes/summary/coverageIcons'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { COVERAGE_TYPE_LABELS } from '@/models/insuranceCoverage'
import {
  BUSINESS_DETAILS,
  CLAIMS_STATE_COPY,
  COVERAGE_NEEDS,
  DRIVING_DISTANCE,
  FREIGHT_TYPES,
  OWNER_INFO,
} from './confirmInfoData'

/** Prototype scaffolding — parsing is transient in the real flow, so it needs a way in. */
const CLAIMS_STATES = [
  { value: 'parsed', label: 'Parsed' },
  { value: 'single', label: 'Single' },
  { value: 'clean', label: 'Clean' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'parsing', label: 'Parsing' },
]

/**
 * Renders the same SectionCard / InfoRow / IllustratedRow the application uses, on mock data —
 * so a change to the real cards shows up here without being made twice. Only the data-fetching
 * wrappers (ChatAddDrivers, ChatAddEquipments) are stood in for.
 */
export function ConfirmInfoCards() {
  const [claimsState, setClaimsState] = useState('parsed')
  const isParsing = claimsState === 'parsing'
  const claims = CLAIMS_STATE_COPY[claimsState]
  const isSkipped = claimsState === 'skipped'

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-2">
      <div className="mb-4 flex items-center gap-3">
        <span className="text-ink-400 font-mono text-[10px] tracking-[0.12em] uppercase">Claims state</span>
        <SegmentedControl value={claimsState} onValueChange={setClaimsState} options={CLAIMS_STATES} />
      </div>
      <SectionCard title="Business details" action={<EditButton />}>
        {BUSINESS_DETAILS.map((row) => (
          <InfoRow key={row.label} label={row.label} value={row.value} />
        ))}
      </SectionCard>

      <SectionCard title="Coverage needs" action={<EditButton />}>
        <InfoRow label="Coverage starts" value={COVERAGE_NEEDS.startsOn} />
        <div className="flex flex-col gap-2">
          {COVERAGE_NEEDS.coverages.map((coverage) => {
            const Icon = COVERAGE_ICONS[coverage]
            return (
              <span key={coverage} className="text-foreground flex items-center gap-2">
                <Icon className="text-ink-500 size-5 shrink-0" />
                {COVERAGE_TYPE_LABELS[coverage]}
              </span>
            )
          })}
        </div>
      </SectionCard>

      <SectionCard title="Owner info" action={<EditButton />}>
        {OWNER_INFO.map((row) => (
          <InfoRow key={row.label} label={row.label} value={row.value} />
        ))}
      </SectionCard>

      <SectionCard title="Driving distance" action={<EditButton />}>
        <div className="flex flex-col gap-1">
          {DRIVING_DISTANCE.map((distance) => (
            <span key={distance} className="text-foreground">
              {distance}
            </span>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Freight details" action={<EditButton />}>
        <div className="flex flex-col gap-1">
          {FREIGHT_TYPES.map((item) => (
            <span key={item.name} className="text-foreground">
              {item.name} ({item.percentage}%)
            </span>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Claims"
        action={<EditButton />}
        loading={isParsing}
        warning={isSkipped}
        footer={claims.footer}
      >
        <IllustratedRow
          illustration={IllustrationName.LOSS_RUN}
          muted={isSkipped}
          label={
            <span className={isSkipped ? 'text-muted-foreground' : 'text-foreground'}>
              {claims.body.map((line, index) => (
                <Fragment key={line}>
                  {index > 0 && <br />}
                  {line}
                </Fragment>
              ))}
            </span>
          }
        />
      </SectionCard>

      <SectionCard title="Drivers & equipment">
        <div className="flex flex-col gap-3">
          <IllustratedRow
            illustration={IllustrationName.DRIVER}
            label="4 active drivers"
            action={<EditButton />}
          />
          <IllustratedRow
            illustration={IllustrationName.TRUCK}
            label="3 active trucks"
            action={<EditButton />}
          />
          <IllustratedRow
            illustration={IllustrationName.TRAILER}
            label="2 active trailers"
            action={<EditButton />}
          />
        </div>
      </SectionCard>

      <div className="mt-8 flex flex-col gap-2">
        <Card className="w-full">
          <CardContent>
            <IllustratedRow
              illustration={IllustrationName.BOR}
              label="Broker of Record authorization form"
              labelClassName="font-semibold"
              action={<Button>Sign</Button>}
            />
          </CardContent>
        </Card>
        <Card className="w-full">
          <CardContent>
            <IllustratedRow
              illustration={IllustrationName.LOSS_RUN}
              label="Submit your loss run reports"
              labelClassName="font-semibold"
              action={<Button>Upload</Button>}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
